import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, X, Instagram, Facebook } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SOCIAL_LINKS, TikTokIcon } from "./social";
import { LocalizedLink } from "../../lib/i18nRouting";
import { localizedPath, DEFAULT_LANG, stripLangPrefix } from "../../i18n";
import LanguageSwitcher from "./LanguageSwitcher";
import { HOLIDAYS_PATH } from "../../data/holidays";

// Pages that start on a light (bone) background — header must use light tone from scroll=0.
const LIGHT_TOP_PATHS = ["/experiente", "/rezervare", "/contact"];
function isLightTopPath(pathname) {
  const clean = stripLangPrefix(pathname);
  if (clean === "/experiente") return true;
  if (clean.startsWith("/experiente/")) return true;
  return LIGHT_TOP_PATHS.includes(clean);
}

export default function Header() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const lang = i18n.language || DEFAULT_LANG;
  const forceLight = isLightTopPath(location.pathname);
  const showLight = scrolled || open || forceLight;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const NAV = [
    { to: "/", label: t("nav.home"), key: "home" },
    { to: "/experiente", label: t("nav.experiences"), key: "experiences" },
    { to: "/apuseni", label: t("nav.apuseni"), key: "apuseni" },
    { to: HOLIDAYS_PATH, label: t("nav.holidays"), shortLabel: t("nav.holidays_short"), key: "holidays" },
    { to: "/contact", label: t("nav.contact"), key: "contact" },
  ];

  const tone = showLight ? "light" : "dark"; // switcher color context

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        showLight
          ? "bg-bone/95 backdrop-blur border-b border-bone-deep"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between gap-6">
        <LocalizedLink to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <span className={`inline-block w-2 h-2 ${showLight ? "bg-forest" : "bg-bone"}`} />
          <span
            className={`font-display text-xl tracking-tight transition-colors whitespace-nowrap ${
              showLight ? "text-forest" : "text-bone"
            }`}
          >
            Apuseni Active
          </span>
        </LocalizedLink>

        <nav className="hidden xl:flex items-center gap-4">
          {NAV.map((item) => (
            <NavLink
              key={item.key}
              to={localizedPath(item.to, lang)}
              data-testid={`nav-${item.key}`}
              end={item.to === "/"}
              className={({ isActive }) =>
                `text-xs uppercase tracking-wider-3 link-underline transition-colors whitespace-nowrap ${
                  showLight ? "text-forest" : "text-bone"
                } ${isActive ? "opacity-100" : "opacity-80 hover:opacity-100"}`
              }
            >
              {item.shortLabel || item.label}
            </NavLink>
          ))}

          <div className={`flex items-center gap-3 pl-2 border-l transition-colors ${showLight ? "border-bone-deep" : "border-bone/25"}`}>
            <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="header-social-instagram" className={`opacity-70 hover:opacity-100 hover:-translate-y-0.5 transition-all duration-300 ${showLight ? "text-forest" : "text-bone"}`}><Instagram className="w-4 h-4 stroke-[1.5]" /></a>
            <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="header-social-facebook" className={`opacity-70 hover:opacity-100 hover:-translate-y-0.5 transition-all duration-300 ${showLight ? "text-forest" : "text-bone"}`}><Facebook className="w-4 h-4 stroke-[1.5]" /></a>
            <a href={SOCIAL_LINKS.tiktok} target="_blank" rel="noreferrer" aria-label="TikTok" data-testid="header-social-tiktok" className={`opacity-70 hover:opacity-100 hover:-translate-y-0.5 transition-all duration-300 ${showLight ? "text-forest" : "text-bone"}`}><TikTokIcon className="w-4 h-4" strokeWidth={1.5} /></a>
          </div>

          <LanguageSwitcher tone={tone === "light" ? "light" : "dark"} compact />

          <LocalizedLink
            to="/rezervare"
            data-testid="header-cta-book"
            className={`btn-lift inline-flex items-center px-5 py-3 text-xs uppercase tracking-wider-3 ${
              showLight
                ? "bg-forest text-bone hover:bg-forest-deep"
                : "bg-bone text-forest hover:bg-bone-deep"
            }`}
          >
            {t("nav.book")}
          </LocalizedLink>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className={`xl:hidden p-2 ${showLight ? "text-forest" : "text-bone"}`}
          onClick={() => setOpen((o) => !o)}
          aria-label={t("nav.menu")}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden bg-bone border-t border-bone-deep" data-testid="mobile-menu">
          <div className="px-6 py-6 space-y-5">
            {NAV.map((item) => (
              <NavLink
                key={item.key}
                to={localizedPath(item.to, lang)}
                end={item.to === "/"}
                data-testid={`mobile-nav-${item.key}`}
                className="block text-forest text-base uppercase tracking-wider-2"
              >
                {item.label}
              </NavLink>
            ))}

            <div className="pt-4">
              <LanguageSwitcher tone="light" />
            </div>

            <LocalizedLink
              to="/rezervare"
              data-testid="mobile-cta-book"
              className="block bg-forest text-bone text-center px-6 py-4 text-xs uppercase tracking-wider-3"
            >
              {t("nav.book")}
            </LocalizedLink>

            <div className="flex items-center gap-6 pt-5 border-t border-bone-deep">
              <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="mobile-social-instagram" className="text-forest opacity-70 hover:opacity-100 transition-opacity"><Instagram className="w-5 h-5 stroke-[1.5]" /></a>
              <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="mobile-social-facebook" className="text-forest opacity-70 hover:opacity-100 transition-opacity"><Facebook className="w-5 h-5 stroke-[1.5]" /></a>
              <a href={SOCIAL_LINKS.tiktok} target="_blank" rel="noreferrer" aria-label="TikTok" data-testid="mobile-social-tiktok" className="text-forest opacity-70 hover:opacity-100 transition-opacity"><TikTokIcon className="w-5 h-5" strokeWidth={1.5} /></a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
